import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { get, post } from "../../utils/apiManager";
import { baseURL } from "../../config/config";
import { toast } from "react-toastify";
import Swal from 'sweetalert2';
import Pagination from 'react-js-pagination';
import { useForm } from "react-hook-form";
import SuccessModal from "../../components/SuccessModal";

const Index = () => {
  const { register, handleSubmit, getValues, setValue } = useForm();
  const [companies, setCompanies] = useState([]);
  const Navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [statusValue, setStatusValue] = useState("");
  const [pageData, setPageData] = useState({ page: 1, perPage: 25 });
  const [modalData, setModalData] = useState({ message: "", show: false });
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = useRef({});
  // Filters, e.g. to find junk sign-ups: no plan, no card, email not verified, no sales, older than a week
  const NO_FILTERS = { billing: "", card: "", verified: "", sales: "", olderThanDays: "" };
  const JUNK_FILTERS = { billing: "noPlan", card: "no", verified: "no", sales: "none", olderThanDays: "7" };
  const [filters, setFilters] = useState(NO_FILTERS);
  const [selected, setSelected] = useState([]);
  const setFilter = (key, value) => { setFilters((f) => ({ ...f, [key]: value })); setPageData((p) => ({ ...p, page: 1 })); };

  useEffect(() => { getAllCompanies(); }, [pageData, statusValue, filters]);

  useEffect(() => {
    const handleClick = (e) => {
      if (openDropdown !== null) {
        const ref = dropdownRefs.current[openDropdown];
        if (ref && !ref.contains(e.target)) setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openDropdown]);

  const getAllCompanies = (searchVal, statusVal) => {
    const searchItem = searchVal !== undefined ? searchVal : (getValues("companyName") || "");
    const sValue = statusVal !== undefined ? statusVal : statusValue;
    const q = new URLSearchParams({ page: String(pageData.page), perPage: String(pageData.perPage), searchItem, statusValue: sValue || "" });
    Object.entries(filters).forEach(([k, v]) => { if (v) q.set(k, v); });
    get(`/company?${q.toString()}`)
      .then((res) => { setCompanies(res?.data || []); setCount(res?.pages || 0); setSelected([]); })
      .catch((err) => console.log(err));
  };

  const changeStatus = (id, status) => {
    post(`${baseURL}/company/status`, { id, status: !status })
      .then((res) => { if (res?.status) { setModalData({ message: res.message, show: true }); getAllCompanies(); } else toast.error(res?.message); })
      .catch((err) => toast.error(err?.response?.data?.error));
    setOpenDropdown(null);
  };

  const deleteHandler = (id) => {
    setOpenDropdown(null);
    Swal.fire({ title: 'Are you sure?', text: 'This will delete the company.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#AD2429', cancelButtonColor: '#6b7a99', confirmButtonText: 'Yes, delete it!' })
      .then((result) => {
        if (result.isConfirmed) {
          post(`${baseURL}/company/delete`, { id })
            .then((res) => { if (res?.status) { setModalData({ message: res.message, show: true }); getAllCompanies(); } else toast.error(res?.message); })
            .catch((err) => toast.error(err?.response?.data?.error));
        }
      });
  };

  const verifiedHandler = (id) => {
    post(`${baseURL}/company/verified`, { id })
      .then((res) => { if (res?.status) { setModalData({ message: res.message, show: true }); getAllCompanies(); } else toast.error(res?.message); })
      .catch((err) => toast.error(err?.response?.data?.error));
    setOpenDropdown(null);
  };

  // Emails the company a link to set a new password (their current password keeps working until then)
  const sendCredentials = (item) => {
    setOpenDropdown(null);
    Swal.fire({ title: 'Send a password link?', text: `${item?.email || 'The company'} will get an email with a link to set a new password. Their current password keeps working until they use it.`, icon: 'question', showCancelButton: true, confirmButtonColor: '#AD2429', cancelButtonColor: '#6b7a99', confirmButtonText: 'Send link' })
      .then((result) => {
        if (!result.isConfirmed) return;
        post(`${baseURL}/company/send-credentials`, { id: item._id })
          .then((res) => { if (res?.status) setModalData({ message: res.message, show: true }); else toast.error(res?.message); })
          .catch((err) => toast.error(err?.response?.data?.error));
      });
  };

  const deleteSelected = () => {
    if (!selected.length) return;
    Swal.fire({ title: `Delete ${selected.length} compan${selected.length === 1 ? 'y' : 'ies'}?`, text: 'They are removed from the list and can no longer sign in. Companies with a plan in use (paid, trial or payment failed) are skipped.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#AD2429', cancelButtonColor: '#6b7a99', confirmButtonText: 'Yes, delete them' })
      .then((result) => {
        if (!result.isConfirmed) return;
        post(`${baseURL}/company/delete-many`, { ids: selected })
          .then((res) => { if (res?.status) { setModalData({ message: res.message, show: true }); getAllCompanies(); } else toast.error(res?.message); })
          .catch((err) => toast.error(err?.response?.data?.error));
      });
  };

  const toggleSelected = (id) => setSelected((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));
  const allSelected = companies?.length > 0 && companies.every((c) => selected.includes(c._id));

  const BILLING_LABELS = {
    paying: ['Paid', '#15803d', '#dcfce7'],
    trial: ['Trial', '#1d4ed8', '#dbeafe'],
    paymentFailed: ['Payment failed', '#b91c1c', '#fee2e2'],
    canceled: ['Canceled', '#64748b', '#f1f5f9'],
    trialExpired: ['Trial expired', '#b45309', '#fef3c7'],
    noPlan: ['No plan', '#64748b', '#f1f5f9'],
    demo: ['Demo', '#b45309', '#fffbeb'],
    free: ['Free', '#6d28d9', '#ede9fe'],
    other: ['Incomplete', '#64748b', '#f1f5f9'],
  };

  const toggleWhiteLabel = (item) => {
    const next = !item?.whiteLabelEnabled;
    post('/company/white-label-toggle', { id: item._id, whiteLabelEnabled: next })
      .then((res) => {
        if (res?.status) {
          setModalData({ message: res.message || (next ? 'White Label enabled' : 'White Label disabled'), show: true });
          getAllCompanies();
        } else {
          toast.error(res?.message || 'Failed to update White Label');
        }
      })
      .catch((err) => toast.error(err?.response?.data?.error || err?.message || 'Failed to update White Label'));
    setOpenDropdown(null);
  };

  // Free account: no plan, no card, no subscription charges, no sale fees (controllers/adminFreeAccount.js)
  const toggleFreeAccount = (item) => {
    setOpenDropdown(null);
    const name = item?.companyName || item?.email || 'this company';
    if (!item?.isFreeAccount) {
      Swal.fire({
        title: `Make ${name} a free account?`,
        html: 'They will not choose a plan or need a card, and will not be charged a subscription or any sale fees.<br/><br/>Nothing is changed in Stripe. If they already have a paid subscription you will be told its id so you can cancel it there.',
        input: 'text',
        inputPlaceholder: 'Note (optional), e.g. why it is free',
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#6d28d9',
        cancelButtonColor: '#6b7a99',
        confirmButtonText: 'Make free',
      }).then((result) => {
        if (!result.isConfirmed) return;
        post('/company/free-account', { id: item._id, isFree: true, note: result.value || '' })
          .then((res) => {
            if (res?.status) {
              setModalData({ message: res.message || 'Free account turned on', show: true });
              getAllCompanies();
            } else {
              toast.error(res?.message || 'Could not make it a free account');
            }
          })
          .catch((err) => toast.error(err?.response?.data?.message || err?.message || 'Could not make it a free account'));
      });
      return;
    }
    Swal.fire({
      title: `End the free account for ${name}?`,
      text: 'They will be asked to choose a plan (and add a card) the next time they open their admin. Sale fees already waived stay waived.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#AD2429',
      cancelButtonColor: '#6b7a99',
      confirmButtonText: 'End free account',
    }).then((result) => {
      if (!result.isConfirmed) return;
      post('/company/free-account', { id: item._id, isFree: false })
        .then((res) => {
          if (res?.status) {
            setModalData({ message: res.message || 'Free account ended', show: true });
            getAllCompanies();
          } else {
            toast.error(res?.message || 'Could not end the free account');
          }
        })
        .catch((err) => toast.error(err?.response?.data?.message || err?.message || 'Could not end the free account'));
    });
  };

  const pill = (label, color, bg) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 100, fontSize: 11, fontWeight: 700, background: bg, color }}>
      <span style={{ fontSize: 7 }}>●</span>{label}
    </span>
  );

  const thS = (align = 'left') => ({ padding: '12px 16px', fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#9aa5c0', textAlign: align, borderBottom: '1px solid #edf0f7', background: '#f8f9fc', whiteSpace: 'nowrap' });
  const tdS = (align = 'left') => ({ padding: '12px 16px', fontSize: 14, color: '#3a4a6a', borderBottom: '1px solid #f0f2f8', verticalAlign: 'middle', textAlign: align });

  const menuItem = (icon, label, color, onClick) => (
    <button onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 14px', border: 'none', background: 'none', fontSize: 13, color: color || '#3a4a6a', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', transition: 'background .1s' }}
      onMouseEnter={e => e.currentTarget.style.background = '#f8f9fc'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <span>{icon}</span>{label}
    </button>
  );

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #edf0f7', boxShadow: '0 1px 4px rgba(26,39,68,.05)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #edf0f7' }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0a1628', margin: 0 }}>All Companies</h3>
            <p style={{ fontSize: 13, color: '#9aa5c0', margin: '3px 0 0' }}>Registered auction companies</p>
          </div>
          <Link to="/admin/user/add" style={{ textDecoration: 'none' }}>
            <button style={{ background: '#AD2429', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              + Add Company
            </button>
          </Link>
        </div>

        {/* Search bar */}
        <div style={{ padding: '14px 24px', borderBottom: '1px solid #edf0f7', display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'flex-end' }}>
          <input
            placeholder="Search companies..."
            {...register("companyName")}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 14, color: '#0a1628', outline: 'none', width: 220, fontFamily: 'inherit' }}
          />
          <button onClick={() => getAllCompanies()} style={{ background: '#1a2744', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            Search
          </button>
          <button onClick={() => { setValue("companyName", ""); setStatusValue(""); setFilters(NO_FILTERS); }}
            style={{ background: '#f0f2f8', color: '#5a6a8a', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            Clear
          </button>
        </div>

        {/* Filters */}
        <div style={{ padding: '12px 24px', borderBottom: '1px solid #edf0f7', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          {[
            ['billing', 'Paid', [['', 'Any plan status'], ['paid', 'Paid'], ['trial', 'Trial'], ['failed', 'Payment failed'], ['canceled', 'Canceled'], ['noPlan', 'No plan'], ['demo', 'Demo mode'], ['free', 'Free account']]],
            ['card', 'Card', [['', 'Card: any'], ['yes', 'Card on file'], ['no', 'No card']]],
            ['verified', 'Email', [['', 'Email: any'], ['yes', 'Email verified'], ['no', 'Email not verified']]],
            ['sales', 'Sales', [['', 'Sales: any'], ['none', 'No sales'], ['some', 'Has sales']]],
            ['olderThanDays', 'Signed up', [['', 'Signed up: any time'], ['7', 'Signed up 7+ days ago'], ['30', 'Signed up 30+ days ago'], ['90', 'Signed up 90+ days ago']]],
          ].map(([key, label, options]) => (
            <select key={key} aria-label={label} value={filters[key]} onChange={(e) => setFilter(key, e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, border: `1px solid ${filters[key] ? '#AD2429' : '#e2e8f0'}`, fontSize: 13, color: '#0a1628', background: '#fff', outline: 'none', fontFamily: 'inherit' }}>
              {options.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
          ))}
          <button type="button" onClick={() => { setFilters(JUNK_FILTERS); setPageData((p) => ({ ...p, page: 1 })); }}
            title="No plan, no card, email not verified, no sales, signed up 7+ days ago"
            style={{ background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa', borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
            Likely junk sign-ups
          </button>
          {selected.length > 0 && (
            <button type="button" onClick={deleteSelected}
              style={{ marginLeft: 'auto', background: '#b91c1c', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              Delete selected ({selected.length})
            </button>
          )}
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ ...thS('center'), width: 36 }}>
                  <input type="checkbox" aria-label="Select all on this page" checked={allSelected}
                    onChange={() => setSelected(allSelected ? [] : companies.map((c) => c._id))} />
                </th>
                <th style={{ ...thS('left'), minWidth: 160 }}>Name</th>
                <th style={{ ...thS('left'), minWidth: 140 }}>Company</th>
                <th style={{ ...thS('left'), minWidth: 190 }}>Email</th>
                <th style={{ ...thS('left'), minWidth: 130 }}>Phone</th>
                <th style={{ ...thS('center'), minWidth: 100 }}>Verified</th>
                <th style={{ ...thS('center'), minWidth: 100 }}>Status</th>
                <th style={{ ...thS('center'), minWidth: 90 }}>Card</th>
                <th style={{ ...thS('center'), minWidth: 110 }}>Paid</th>
                <th style={{ ...thS('center'), minWidth: 70 }}>Sales</th>
                <th style={{ ...thS('left'), minWidth: 110 }}>Signed up</th>
                <th style={{ ...thS('center'), minWidth: 110 }}>White Label</th>
                <th style={{ ...thS('center'), minWidth: 80 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {companies?.length > 0 ? companies.map((item, index) => (
                <tr key={item?._id}
                  onMouseEnter={e => e.currentTarget.style.background = '#fafbfd'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={{ ...tdS('center'), width: 36 }}>
                    <input type="checkbox" aria-label={`Select ${item?.companyName || item?.email}`} checked={selected.includes(item._id)} onChange={() => toggleSelected(item._id)} />
                  </td>
                  <td style={{ ...tdS('left'), fontWeight: 600, color: '#0a1628', minWidth: 160 }}>
                    {item?.firstName ? `${item.firstName} ${item.lastName}` : '—'}
                  </td>
                  <td style={{ ...tdS('left'), minWidth: 140 }}>{item?.companyName || '—'}</td>
                  <td style={{ ...tdS('left'), fontSize: 13, minWidth: 190 }}>{item?.email || '—'}</td>
                  <td style={{ ...tdS('left'), minWidth: 130, whiteSpace: 'nowrap' }}>{item?.phone || '—'}</td>
                  <td style={{ ...tdS('center'), minWidth: 100 }}>
                    {item?.isVerified
                      ? pill('Verified', '#15803d', '#dcfce7')
                      : pill('Unverified', '#b91c1c', '#fee2e2')}
                  </td>
                  <td style={{ ...tdS('center'), minWidth: 100 }}>
                    {item?.isActive
                      ? pill('Active', '#15803d', '#dcfce7')
                      : pill('Inactive', '#b91c1c', '#fee2e2')}
                  </td>
                  <td style={{ ...tdS('center'), minWidth: 90 }}>
                    {item?.hasCard ? pill('On file', '#15803d', '#dcfce7') : pill('None', '#64748b', '#f1f5f9')}
                  </td>
                  <td style={{ ...tdS('center'), minWidth: 110 }}>
                    {(() => {
                      const [text, color, bg] = BILLING_LABELS[item?.billingStatus] || BILLING_LABELS.other;
                      const title = item?.billingStatus === 'demo' && item?.demoStartedAt ? `Demo since ${new Date(item.demoStartedAt).toLocaleDateString()}`
                        : item?.billingStatus === 'free' ? `Free since ${item?.freeAccountSince ? new Date(item.freeAccountSince).toLocaleDateString() : '—'}${item?.freeAccountNote ? ` — ${item.freeAccountNote}` : ''}`
                        : (item?.subscriptionStatus || '');
                      return <span title={title}>{pill(text, color, bg)}</span>;
                    })()}
                  </td>
                  <td style={{ ...tdS('center'), minWidth: 70 }}>{item?.saleCount || 0}</td>
                  <td style={{ ...tdS('left'), minWidth: 110, whiteSpace: 'nowrap', fontSize: 13 }}>{item?.createdAt ? new Date(item.createdAt).toLocaleDateString() : '—'}</td>
                  <td style={{ ...tdS('center'), minWidth: 110 }}>
                    {item?.whiteLabelEnabled
                      ? pill('Enabled', '#15803d', '#dcfce7')
                      : pill('Off', '#64748b', '#f1f5f9')}
                  </td>
                  <td style={{ ...tdS('center'), minWidth: 80 }}>
                    <div ref={el => dropdownRefs.current[index] = el} style={{ position: 'relative', display: 'inline-block' }}>
                      <button
                        onClick={() => setOpenDropdown(openDropdown === index ? null : index)}
                        style={{ background: '#f0f2f8', border: 'none', borderRadius: 7, width: 32, height: 32, cursor: 'pointer', fontSize: 16, color: '#5a6a8a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >⋯</button>
                      {openDropdown === index && (
                        <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 4px)', background: '#fff', borderRadius: 10, boxShadow: '0 8px 28px rgba(26,39,68,.12), 0 0 0 1px rgba(26,39,68,.06)', minWidth: 190, zIndex: 200, overflow: 'hidden' }}>
                          {!item?.isVerified && menuItem('✓', 'Verify Company', '#15803d', () => verifiedHandler(item._id))}
                          {menuItem(item?.isActive ? '✕' : '✓', item?.isActive ? 'Deactivate' : 'Activate', item?.isActive ? '#b91c1c' : '#15803d', () => changeStatus(item._id, item?.isActive))}
                          {menuItem('✎', 'Edit', '#1a2744', () => { Navigate(`/admin/user/edit?${item._id}`); setOpenDropdown(null); })}
                          {menuItem('👁', 'View Details', '#1a2744', () => { Navigate(`/admin/user/view?${item._id}`); setOpenDropdown(null); })}
                          {menuItem('🎨', item?.whiteLabelEnabled ? 'Disable White Label' : 'Enable White Label', item?.whiteLabelEnabled ? '#b91c1c' : '#15803d', () => toggleWhiteLabel(item))}
                          {menuItem('★', item?.isFreeAccount ? 'End Free Account' : 'Make Free Account', item?.isFreeAccount ? '#b91c1c' : '#6d28d9', () => toggleFreeAccount(item))}
                          {menuItem('🧾', 'New Invoice', '#1a2744', () => { Navigate(`/admin/invoices/edit?companyId=${item._id}`); setOpenDropdown(null); })}
                          {menuItem('✉', 'Send Password Link', '#1a2744', () => sendCredentials(item))}
                          {menuItem('🔑', 'Change Password', '#1a2744', () => { Navigate(`/admin/change-password?${item._id}`); setOpenDropdown(null); })}
                          {/* Company copies of email templates are no longer used; companies set a signature in Email Settings */}
                          {/* {menuItem('📧', 'Email Templates', '#1a2744', () => { Navigate(`/admin/email-template?${item._id}`); setOpenDropdown(null); })} */}
                          <div style={{ borderTop: '1px solid #f0f2f8' }}>
                            {menuItem('🗑', 'Delete', '#b91c1c', () => deleteHandler(item._id))}
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={13} style={{ ...tdS, textAlign: 'center', padding: '48px', color: '#9aa5c0' }}>
                    No companies found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {companies?.length > 0 && (
          <div style={{ padding: '14px 24px', borderTop: '1px solid #edf0f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 13, color: '#9aa5c0' }}>
              Page {pageData.page} · {pageData.perPage} per page
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Pagination
                itemClass="page-item"
                linkClass="page-link"
                activePage={pageData.page}
                itemsCountPerPage={pageData.perPage}
                totalItemsCount={count}
                pageRangeDisplayed={5}
                onChange={(page) => setPageData(prev => ({ ...prev, page }))}
              />
              <select
                onChange={(e) => setPageData(prev => ({ ...prev, perPage: e.target.value, page: 1 }))}
                style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 13, color: '#3a4a6a', background: '#fff', fontFamily: 'inherit' }}
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="75">75</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
        )}
      </div>

      <SuccessModal message={modalData?.message} showModal={modalData?.show} setShowModal={setModalData} />
    </div>
  );
};

export default Index;
